import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { signout } from "../redux/authentication/AuthenticationSlice";
import Editprofile from "./account/Editprofile";
import Address from "./account/Address";
// Import React Icons
import {
  FaUser,
  FaMapMarkerAlt,
  FaShoppingCart,
  FaCut,
  FaBox,
  FaCalendarCheck,
  FaShieldAlt,
  FaInfoCircle,
  FaFileAlt,
  FaShareAlt,
  FaSignOutAlt,
  FaChevronRight,
  FaUser as FaUserFill,
  FaExternalLinkAlt,
  FaShoppingBag,
  FaCalendarAlt,
  FaFileContract,
  FaShareSquare,
} from "react-icons/fa";
import { HiShoppingCart, HiScissors } from "react-icons/hi";
import { FaShield } from "react-icons/fa6";
import ShopOrder from "./account/ShopOrder";
import SalonOrders from "./account/SalonOrders";
import Privacy from "./Privacy";
import About from "./About";
import TermsNdConditions from "./TermsNdConditions";

const SaloneAccount = () => {
  const { loginData } = useSelector((state) => state.Authentication);
  const { CartItems } = useSelector((state) => state.Cart);
  const { salonHomeCartItems } = useSelector((state) => state.HomeSalonCart);
  const { OrderTotal } = useSelector((state) => state.Order);
  const { homeSalonOrders } = useSelector((state) => state.OrderSalon);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [userInfo, setUserInfo] = useState(loginData);
  const [shopCartCount, setShopCartCount] = useState(CartItems?.length || 0);
  const [salonCartCount, setSalonCartCount] = useState(
    salonHomeCartItems?.length || 0
  );
  const [shopOrdersCount, setShopOrdersCount] = useState(
    OrderTotal?.length || 0
  );
  const [salonOrdersCount, setSalonOrdersCount] = useState(
    homeSalonOrders?.length || 0
  );
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [activeView, setActiveView] = useState("editProfile");
  const [isMobile, setIsMobile] = useState(false);

  // Check screen size
  useEffect(() => {
    const checkScreenSize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkScreenSize();
    window.addEventListener("resize", checkScreenSize);

    return () => window.removeEventListener("resize", checkScreenSize);
  }, []);

  // Update when redux data changes
  useEffect(() => {
    setUserInfo(loginData);
  }, [loginData]);

  useEffect(() => {
    setShopCartCount(CartItems.length);
  }, [CartItems]);

  useEffect(() => {
    setSalonCartCount(salonHomeCartItems?.length || 0);
  }, [salonHomeCartItems]);

  useEffect(() => {
    setShopOrdersCount(OrderTotal?.length || 0);
  }, [OrderTotal]);

  useEffect(() => {
    setSalonOrdersCount(homeSalonOrders?.length || 0);
  }, [homeSalonOrders]);

  const signoutPress = () => {
    setShowLogoutModal(true);
  };

  const confirmLogout = () => {
    setShowLogoutModal(false);
    dispatch(signout());
    navigate("/login");
  };

  const cancelLogout = () => {
    setShowLogoutModal(false);
  };

  const onShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: "SalonTreat",
          text: "Check out SalonTreat!",
          url: "https://play.google.com/store/apps/details?id=com.salontreat.app",
        });
      } else {
        await navigator.clipboard.writeText(
          "https://play.google.com/store/apps/details?id=com.salontreat"
        );
        alert("Link copied to clipboard!");
      }
    } catch (error) {
      console.error("Error sharing:", error);
    }
  };

  // MOBILE MENU SECTIONS (EXACTLY LIKE REACT NATIVE)
  const mobileMenuSections = [
    {
      title: "User",
      data: [
        {
          icon: <FaUser />,
          title: "Edit Profile",
          onPress: () => navigate("/editprofile"),
        },
      ],
    },
    {
      title: "Address",
      data: [
        {
          icon: <FaMapMarkerAlt />,
          title: "Addresses",
          onPress: () => navigate("/addresses"),
        },
      ],
    },
    {
      title: "Orders & Appointments",
      data: [
        {
          icon: <FaBox />,
          title: `Shop Orders (${shopOrdersCount})`,
          onPress: () => navigate("/ShopOrderaccount"),
          show: true,
        },
        {
          icon: <FaCalendarCheck />,
          title: `Salon Orders (${salonOrdersCount})`,
          onPress: () => navigate("/salonOrderaccount"),
          show: true,
        },
      ],
    },
    {
      title: "Support",
      data: [
        {
          icon: <FaShieldAlt />,
          title: "Privacy Policy",
          onPress: () => navigate("/privacy-policy"),
        },
        {
          icon: <FaInfoCircle />,
          title: "About Us",
          onPress: () => navigate("/about-us"),
        },
        {
          icon: <FaFileAlt />,
          title: "Terms and Conditions",
          onPress: () => navigate("/terms-and-conditions"),
        },
        {
          icon: <FaShareAlt />,
          title: "Share with Friends & Family",
          onPress: onShare,
        },
      ],
    },
    {
      title: "Account",
      data: [
        {
          icon: <FaSignOutAlt />,
          title: "Logout",
          onPress: signoutPress,
          isLogout: true,
        },
      ],
    },
  ];

  // DESKTOP SIDEBAR ITEMS WITH ICONS
  const desktopSidebarItems = [
    {
      sectionTitle: "Navigate",
      items: [
        {
          title: "Shop Orders",
          icon: <FaShoppingBag size={18} />,
          view: "shopOrders",
          navigateTo: "/ShopOrderaccount",
          onPress: () =>
            isMobile
              ? navigate("/ShopOrderaccount")
              : setActiveView("shopOrders"),
        },
        {
          title: "Salon Orders",
          icon: <FaCalendarAlt size={18} />,
          view: "salonOrders",
          navigateTo: "/salonOrderaccount",
          onPress: () =>
            isMobile
              ? navigate("/salonOrderaccount")
              : setActiveView("salonOrders"),
        },
      ],
    },
    {
      sectionTitle: "Account",
      items: [
        {
          title: "Edit Profile",
          icon: <FaUser size={18} />,
          view: "editProfile",
          navigateTo: "/editprofile",
          onPress: () =>
            isMobile ? navigate("/editprofile") : setActiveView("editProfile"),
        },
        {
          title: "Addresses",
          icon: <FaMapMarkerAlt size={18} />,
          view: "addresses",
          navigateTo: "/addresses",
          onPress: () =>
            isMobile ? navigate("/addresses") : setActiveView("addresses"),
        },
      ],
    },
    {
      sectionTitle: "More",
      items: [
        {
          title: "Privacy Policy",
          icon: <FaShield size={18} />,
          view: "privacyPolicy",
          navigateTo: "/privacy-policy",
          onPress: () =>
            isMobile
              ? navigate("/privacy-policy")
              : setActiveView("privacyPolicy"),
        },
        {
          title: "About Us",
          icon: <FaInfoCircle size={18} />,
          view: "about",
          navigateTo: "/about-us",
          onPress: () =>
            isMobile ? navigate("/about-us") : setActiveView("about"),
        },
        {
          title: "Terms & Conditions",
          icon: <FaFileContract size={18} />,
          view: "terms",
          navigateTo: "/terms-conditions",
          onPress: () =>
            isMobile
              ? navigate("/terms-conditions")
              : setActiveView("terms"),
        },
        {
          title: "Share App",
          icon: <FaShareSquare size={18} />,
          view: "share",
          navigateTo: null,
          onPress: onShare,
        },
        {
          title: "Logout",
          icon: <FaSignOutAlt size={18} />,
          view: "logout",
          isLogout: true,
          navigateTo: null,
          onPress: signoutPress,
        },
      ],
    },
  ];

  // RENDER MOBILE VIEW (EXACT REACT NATIVE DESIGN)
  const renderMobileView = () => {
    return (
      <div
        className="d-md-none"
        style={{
          backgroundColor: "#f5f6fb",
          minHeight: "100vh",
          paddingBottom: "1rem",
        }}
      >
        {/* Header Section - Pink background like React Native */}
        <div
          style={{
            backgroundColor: "#aa2b57",
            borderRadius: "12px",
            margin: "1.25rem auto",
            padding: "1.25rem",
            width: "90%",
            height: "15vh",
            display: "flex",
            alignItems: "center",
            marginTop: "3rem",
          }}
        >
          <div style={{ marginRight: "0.75rem" }}>
            <div
              style={{
                width: "56px",
                height: "56px",
                borderRadius: "50%",
                backgroundColor: "#CBD2E0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaUserFill size={24} style={{ color: "#1E2A44" }} />
            </div>
          </div>

          <div style={{ flex: 1 }}>
            <h6
              style={{
                fontSize: "1.1rem",
                color: "#fff",
                fontWeight: "700",
                margin: 0,
              }}
            >
              {userInfo?.Name || "Guest User"}
            </h6>
            <p
              style={{
                fontSize: "0.9rem",
                color: "#E0E6F2",
                margin: "2px 0 0 0",
              }}
            >
              +91-{userInfo?.Mobile || "0000000000"}
            </p>
            <div
              style={{
                color: "#fff",
                fontSize: "0.9rem",
                marginTop: "0.25rem",
                fontWeight: "600",
                backgroundColor: "rgba(0,0,0,0.2)",
                alignSelf: "flex-start",
                padding: "0.25rem 0.5rem",
                borderRadius: "4px",
                display: "inline-block",
              }}
            >
              ST Coins:{" "}
              <span style={{ color: "#fff" }}>{userInfo?.StCoins || 0}</span>
            </div>
          </div>
        </div>

        {/* Menu Sections */}
        <div style={{ padding: "0 1rem" }}>
          {mobileMenuSections.map((section, sectionIndex) => (
            <div
              key={sectionIndex}
              style={{
                backgroundColor: "#fff",
                borderRadius: "10px",
                margin: "0.5rem 0",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  fontSize: "0.95rem",
                  fontWeight: "700",
                  color: "#555",
                  padding: "0.75rem 1rem",
                  backgroundColor: "#fff",
                  borderBottom: "1px solid #E5E5E5",
                }}
              >
                {section.title}
              </div>

              <div style={{ padding: "0.25rem 0" }}>
                {section.data.map((item, itemIndex) => {
                  if (item.show === false) return null;

                  return (
                    <button
                      key={itemIndex}
                      onClick={item.onPress}
                      style={{
                        width: "100%",
                        backgroundColor: "transparent",
                        border: "none",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        padding: "0.75rem 1rem",
                        borderBottom:
                          itemIndex === section.data.length - 1
                            ? "none"
                            : "0.4px solid #E5E5E5",
                        cursor: "pointer",
                        textAlign: "left",
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = "#f8f9fa";
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = "transparent";
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <div
                          style={{
                            fontSize: "1rem",
                            color: item.isLogout ? "#ff4444" : "#555",
                            marginRight: "0.75rem",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "20px",
                          }}
                        >
                          {item.icon}
                        </div>
                        <span
                          style={{
                            fontSize: "1rem",
                            color: item.isLogout ? "#ff4444" : "#222",
                            marginLeft: "0.5rem",
                            fontWeight: item.isLogout ? "600" : "500",
                          }}
                        >
                          {item.title}
                        </span>
                      </div>
                      <FaChevronRight
                        size={16}
                        style={{
                          color: item.isLogout ? "#ff4444" : "#777",
                        }}
                      />
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // RENDER DESKTOP SIDEBAR
  const renderDesktopSidebar = () => {
    return (
      <div
        className="col-12 col-md-3 col-lg-3 border-end bg-white p-3 d-none d-md-block overflow-y-scroll "
        style={{
          minHeight: "100vh",
          position: "sticky",
          top: "0",
          alignSelf: "flex-start",
        }}
      >
        {/* User Info */}
        <div className="d-flex align-items-center mb-4">
          <div
            className="rounded-circle d-flex align-items-center justify-content-center me-2"
            style={{
              width: "45px",
              height: "45px",
              backgroundColor: "#1469A7",
            }}
          >
            <span className="text-white fw-bold">
              {userInfo?.Name?.charAt(0) || "U"}
            </span>
          </div>
          <div>
            <h6 className="mb-0" style={{ color: "#1469A7" }}>
              {userInfo?.Name || "Guest User"}
            </h6>
            <small className="text-muted">
              +91-{userInfo?.Mobile || "0000000000"}
            </small>
          </div>
        </div>

        {/* ST Coins Badge */}
        <div
          className="row align-items-center mb-4 p-3 rounded text-start"
          style={{ backgroundColor: "#f0f8ff" }}
        >
          <div className="col-6 text-muted " style={{ whiteSpace: "nowrap" }}>
            ST Coins
          </div>
          <div className="col-6 fw-bold text-end" style={{ color: "#1469A7" }}>
            {userInfo?.StCoins || 0}
          </div>
        </div>

        {/* Navigation Sections */}
        {desktopSidebarItems.map((section, index) => (
          <div key={index}>
            <h6
              className="text-uppercase small mb-2"
              style={{ color: "#1469A7" }}
            >
              {section.sectionTitle}
            </h6>
            <ul
              className={`nav flex-column ${
                index < desktopSidebarItems.length - 1 ? "mb-4" : ""
              }`}
            >
              {section.items.map((item, itemIndex) => {
                const isActive = activeView === item.view;
                return (
                  <li key={itemIndex} className="nav-item">
                    <button
                      className={`nav-link text-start w-100 px-3 py-2 rounded mb-1 d-flex align-items-center justify-content-between ${
                        isActive
                          ? "text-white border-0"
                          : item.isLogout
                          ? "text-danger border-0"
                          : "text-dark border-0"
                      }`}
                      onClick={item.onPress}
                      style={{
                        border: "none",
                        background: isActive ? "#1469A7" : "transparent",
                        cursor: "pointer",
                        textAlign: "left",
                        transition: "all 0.2s ease",
                      }}
                      onMouseEnter={(e) => {
                        if (!isActive && !item.isLogout) {
                          e.currentTarget.style.backgroundColor = "#f0f8ff";
                          e.currentTarget.style.color = "#1469A7";
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isActive && !item.isLogout) {
                          e.currentTarget.style.backgroundColor = "transparent";
                          e.currentTarget.style.color = "#000";
                        }
                      }}
                    >
                      <div className="d-flex align-items-center">
                        <div
                          className="me-2"
                          style={{
                            color: isActive
                              ? "#fff"
                              : item.isLogout
                              ? "#ff4444"
                              : "#555",
                            display: "flex",
                            alignItems: "center",
                          }}
                        >
                          {item.icon}
                        </div>
                        <span
                          style={{
                            color: isActive
                              ? "#fff"
                              : item.isLogout
                              ? "#ff4444"
                              : "#000",
                          }}
                        >
                          {item.title}
                        </span>
                      </div>
                      {item.badge > 0 && (
                        <span
                          className="badge"
                          style={{
                            backgroundColor: "#b93c5b",
                            color: "white",
                          }}
                        >
                          {item.badge}
                        </span>
                      )}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
    );
  };

  // RENDER DESKTOP RIGHT CONTENT
  const renderDesktopRightContent = () => {
    switch (activeView) {
      case "addresses":
        return <Address />;

      case "editProfile":
        return <Editprofile />;

      case "shopOrders":
        return <ShopOrder />;

      case "salonOrders":
        return <SalonOrders />;

      case "privacyPolicy":
        return <Privacy />;

      case "about":
        return <About />;

      case "terms":
        return <TermsNdConditions />;

      default:
        return (
          <div
            className="p-4"
            style={{ backgroundColor: "#f8f9fa", minHeight: "100vh" }}
          >
            <div
              className="d-flex justify-content-center align-items-center"
              style={{ height: "60vh" }}
            >
              <div className="text-center">
                <FaExternalLinkAlt
                  size={48}
                  className="mb-3"
                  style={{ color: "#1469A7" }}
                />
                <h4 style={{ color: "#1469A7" }}>Navigate to Page</h4>
                <p className="text-muted">
                  This content is available on a separate page.
                </p>
                <button
                  className="btn mt-2"
                  onClick={() => {
                    const item = desktopSidebarItems
                      .flatMap((s) => s.items)
                      .find((i) => i.view === activeView);
                    if (item?.navigateTo) navigate(item.navigateTo);
                  }}
                  style={{
                    backgroundColor: "#1469A7",
                    color: "white",
                    border: "none",
                  }}
                >
                  Go to Page
                </button>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="text-start container">
      {/* MOBILE VIEW */}
      {isMobile && renderMobileView()}

      {/* DESKTOP VIEW */}
      {!isMobile && (
        <div className="container-fluid">
          <div className="row">
            {/* Sidebar */}
            {renderDesktopSidebar()}

            {/* Right Content */}
            <div className="col-12 col-md-9 col-lg-9 p-0">
              {renderDesktopRightContent()}
            </div>
          </div>
        </div>
      )}

      {/* Logout Modal (Both Mobile & Desktop) */}
      {showLogoutModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered" style={{marginTop:"40px"}}>
            <div className="modal-content rounded-3" style={{ border: "none" }}>
              <div className="modal-body text-center p-4">
                <div className="mb-3">
                  <FaSignOutAlt
                    size={48}
                    style={{
                      color: "#ff4444",
                    }}
                  />
                </div>
                <h5
                  className="modal-title mb-3 fw-bold"
                  style={{ color: "#333" }}
                >
                  Logout
                </h5>
                <p className="text-muted mb-4">
                  Are you sure you want to logout from your account?
                </p>
                <div className="d-flex gap-3">
                  <button
                    type="button"
                    className="btn flex-fill py-2"
                    onClick={cancelLogout}
                    style={{
                      backgroundColor: "#f8f9fa",
                      borderColor: "#dee2e6",
                      color: "#333",
                      fontWeight: "600",
                      border: "1px solid #dee2e6",
                      borderRadius: "10px",
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn flex-fill py-2"
                    onClick={confirmLogout}
                    style={{
                      backgroundColor: "#ff4444",
                      color: "white",
                      border: "none",
                      fontWeight: "600",
                      borderRadius: "10px",
                    }}
                  >
                    Yes, Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaloneAccount;
